import { c } from "react-compiler-runtime";
import React from "react";
import { PathApi, mergeProps } from "platejs";
import { useEditorReadOnly, useEditorRef, useEditorSelector, useFocused, useIsomorphicLayoutEffect, useOnClickOutside } from "platejs/react";
import { FloatingArrow, FloatingDelayGroup, FloatingFocusManager, FloatingNode, FloatingOverlay, FloatingPortal, FloatingTree, arrow, autoPlacement, autoUpdate, computePosition, detectOverflow, flip, getOverflowAncestors, hide, inline, limitShift, offset, safePolygon, shift, size, useClick, useDelayGroup, useDelayGroupContext, useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useFloatingPortalNode, useFloatingTree, useFocus, useHover, useId, useInteractions, useListNavigation, useRole, useTypeahead } from "@floating-ui/react";

//#region src/createVirtualElement.ts
const getDefaultBoundingClientRect = () => ({
	bottom: 9999,
	height: 0,
	left: -9999,
	right: 9999,
	top: -9999,
	width: 0,
	x: 0,
	y: 0
});
const createVirtualElement = () => ({ getBoundingClientRect: getDefaultBoundingClientRect });

//#endregion
//#region src/hooks/useFloatingToolbar.ts
const useFloatingToolbarState = (t0) => {
	const $ = c(25);
	const { editorId, floatingOptions, focusedEditorId, hideToolbar, showWhenReadOnly } = t0;
	const editor = useEditorRef();
	let t1;
	if ($[0] !== editor.api) {
		t1 = () => editor.api.isExpanded();
		$[0] = editor.api;
		$[1] = t1;
	} else t1 = $[1];
	let t2;
	if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
		t2 = [];
		$[2] = t2;
	} else t2 = $[2];
	const selectionExpanded = useEditorSelector(t1, t2);
	let t3;
	if ($[3] !== editor.api) {
		t3 = () => editor.api.string();
		$[3] = editor.api;
		$[4] = t3;
	} else t3 = $[4];
	let t4;
	if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
		t4 = [];
		$[5] = t4;
	} else t4 = $[5];
	const selectionText = useEditorSelector(t3, t4);
	const readOnly = useEditorReadOnly();
	const focused = useFocused();
	const [open, setOpen] = React.useState(false);
	const [waitForCollapsedSelection, setWaitForCollapsedSelection] = React.useState(false);
	const [mousedown, setMousedown] = React.useState(false);
	let t5;
	if ($[6] !== editor) {
		t5 = () => getSelectionBoundingClientRect(editor);
		$[6] = editor;
		$[7] = t5;
	} else t5 = $[7];
	let t6;
	if ($[8] !== floatingOptions || $[9] !== open || $[10] !== t5) {
		t6 = mergeProps({
			open,
			getBoundingClientRect: t5,
			onOpenChange: setOpen
		}, floatingOptions);
		$[8] = floatingOptions;
		$[9] = open;
		$[10] = t5;
		$[11] = t6;
	} else t6 = $[11];
	const floating = useVirtualFloating(t6);
	let t7;
	if ($[12] !== editorId || $[13] !== floating || $[14] !== focused || $[15] !== focusedEditorId || $[16] !== hideToolbar || $[17] !== mousedown || $[18] !== open || $[19] !== readOnly || $[20] !== selectionExpanded || $[21] !== selectionText || $[22] !== showWhenReadOnly || $[23] !== waitForCollapsedSelection) {
		t7 = {
			editorId,
			floating,
			focused,
			focusedEditorId,
			hideToolbar,
			mousedown,
			open,
			readOnly,
			selectionExpanded,
			selectionText,
			setMousedown,
			setOpen,
			setWaitForCollapsedSelection,
			showWhenReadOnly,
			waitForCollapsedSelection
		};
		$[12] = editorId;
		$[13] = floating;
		$[14] = focused;
		$[15] = focusedEditorId;
		$[16] = hideToolbar;
		$[17] = mousedown;
		$[18] = open;
		$[19] = readOnly;
		$[20] = selectionExpanded;
		$[21] = selectionText;
		$[22] = showWhenReadOnly;
		$[23] = waitForCollapsedSelection;
		$[24] = t7;
	} else t7 = $[24];
	return t7;
};
const useFloatingToolbar = ({ editorId, floating, focusedEditorId, hideToolbar, mousedown, open, readOnly, selectionExpanded, selectionText, setMousedown, setOpen, setWaitForCollapsedSelection, showWhenReadOnly, waitForCollapsedSelection }) => {
	React.useEffect(() => {
		if (!(editorId === focusedEditorId)) setWaitForCollapsedSelection(true);
		if (!selectionExpanded) setWaitForCollapsedSelection(false);
	}, [
		editorId,
		focusedEditorId,
		selectionExpanded,
		setWaitForCollapsedSelection
	]);
	React.useEffect(() => {
		const mouseup = () => setMousedown(false);
		const mousedown$1 = () => setMousedown(true);
		document.addEventListener("mouseup", mouseup);
		document.addEventListener("mousedown", mousedown$1);
		return () => {
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousedown", mousedown$1);
		};
	}, []);
	React.useEffect(() => {
		setOpen((prevOpen) => {
			if (!selectionExpanded || !selectionText || mousedown && !prevOpen || hideToolbar || readOnly && !showWhenReadOnly) return false;
			if (selectionText && selectionExpanded && (!waitForCollapsedSelection || readOnly)) return true;
			return prevOpen;
		});
	}, [
		setOpen,
		editorId,
		focusedEditorId,
		hideToolbar,
		showWhenReadOnly,
		selectionExpanded,
		selectionText,
		mousedown,
		waitForCollapsedSelection,
		open,
		readOnly
	]);
	const { update } = floating;
	useEditorSelector(() => {
		update?.();
	}, [update]);
	return {
		clickOutsideRef: useOnClickOutside(() => {
			setOpen(false);
		}, { ignoreClass: "ignore-click-outside/toolbar" }),
		hidden: !open,
		props: { style: floating.style },
		ref: floating.refs.setFloating
	};
};

//#endregion
//#region src/hooks/useVirtualFloating.ts
/**
* `useFloating` with a controlled virtual element. Used to follow cursor
* position.
*
* Default options:
*
* - `whileElementsMounted: autoUpdate`
*
* Additional options:
*
* - `getBoundingClientRect` to get the bounding client rect.
* - `hidden` to hide the floating element
*
* Additional returns:
*
* - `style` to apply to the floating element
* - `virtualElementRef`
*
* @see useFloating
* @see https://floating-ui.com/docs/react-dom#virtual-element
*/
const useVirtualFloating = (t0) => {
	const $ = c(26);
	let floatingOptions;
	let t1;
	if ($[0] !== t0) {
		({getBoundingClientRect: t1, ...floatingOptions} = t0);
		$[0] = t0;
		$[1] = floatingOptions;
		$[2] = t1;
	} else {
		floatingOptions = $[1];
		t1 = $[2];
	}
	const getBoundingClientRect$1 = t1 === void 0 ? getDefaultBoundingClientRect : t1;
	let t2;
	if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
		t2 = createVirtualElement();
		$[3] = t2;
	} else t2 = $[3];
	const virtualElementRef = React.useRef(t2);
	const [visible, setVisible] = React.useState(true);
	let t3;
	if ($[4] !== floatingOptions) {
		t3 = {
			whileElementsMounted: autoUpdate,
			...floatingOptions
		};
		$[4] = floatingOptions;
		$[5] = t3;
	} else t3 = $[5];
	const floatingResult = useFloating(t3);
	const { middlewareData, refs, strategy, update, x, y } = floatingResult;
	let t4;
	if ($[6] !== getBoundingClientRect$1) {
		t4 = () => {
			virtualElementRef.current.getBoundingClientRect = getBoundingClientRect$1;
		};
		$[6] = getBoundingClientRect$1;
		$[7] = t4;
	} else t4 = $[7];
	let t5;
	if ($[8] !== getBoundingClientRect$1 || $[9] !== update) {
		t5 = [getBoundingClientRect$1, update];
		$[8] = getBoundingClientRect$1;
		$[9] = update;
		$[10] = t5;
	} else t5 = $[10];
	useIsomorphicLayoutEffect(t4, t5);
	let t6;
	let t7;
	if ($[11] !== refs) {
		t6 = () => {
			refs.setReference(virtualElementRef.current);
		};
		t7 = [refs];
		$[11] = refs;
		$[12] = t6;
		$[13] = t7;
	} else {
		t6 = $[12];
		t7 = $[13];
	}
	useIsomorphicLayoutEffect(t6, t7);
	let t8;
	let t9;
	if ($[14] !== middlewareData.hide) {
		t8 = () => {
			if (!middlewareData?.hide) return;
			const { referenceHidden } = middlewareData.hide;
			setVisible(!referenceHidden);
		};
		t9 = [middlewareData.hide];
		$[14] = middlewareData.hide;
		$[15] = t8;
		$[16] = t9;
	} else {
		t8 = $[15];
		t9 = $[16];
	}
	useIsomorphicLayoutEffect(t8, t9);
	const t10 = floatingOptions.open === false ? "none" : void 0;
	const t11 = x ?? 0;
	const t12 = y ?? 0;
	const t13 = visible ? void 0 : "hidden";
	let t14;
	if ($[17] !== strategy || $[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t13) {
		t14 = {
			display: t10,
			left: t11,
			position: strategy,
			top: t12,
			visibility: t13
		};
		$[17] = strategy;
		$[18] = t10;
		$[19] = t11;
		$[20] = t12;
		$[21] = t13;
		$[22] = t14;
	} else t14 = $[22];
	let t15;
	if ($[23] !== floatingResult || $[24] !== t14) {
		t15 = {
			...floatingResult,
			style: t14,
			virtualElementRef
		};
		$[23] = floatingResult;
		$[24] = t14;
		$[25] = t15;
	} else t15 = $[25];
	return t15;
};

//#endregion
//#region src/utils/makeClientRect.ts
const makeClientRect = ({ bottom, left, right, top }) => {
	const width = right - left;
	const props = {
		bottom,
		height: bottom - top,
		left,
		right,
		top,
		width,
		x: left,
		y: top
	};
	return {
		...props,
		toJSON: () => props
	};
};

//#endregion
//#region src/utils/mergeClientRects.ts
const mergeClientRects = (clientRects) => {
	if (clientRects.length === 0) throw new Error("clientRects should not be empty");
	return makeClientRect({
		bottom: Math.max(...clientRects.map((rect) => rect.bottom)),
		left: Math.min(...clientRects.map((rect) => rect.left)),
		right: Math.max(...clientRects.map((rect) => rect.right)),
		top: Math.min(...clientRects.map((rect) => rect.top))
	});
};

//#endregion
//#region src/utils/getBoundingClientRect.ts
const getBoundingClientRect = (editor, at) => {
	const clientRects = (() => {
		if (!at) return [editor.selection].filter(Boolean);
		return (Array.isArray(at) && !PathApi.isPath(at) ? at : [at]).map((location) => editor.api.range(location));
	})().map((range) => editor.api.toDOMRange(range)?.getBoundingClientRect()).filter(Boolean);
	if (clientRects.length === 0) return;
	return mergeClientRects(clientRects);
};

//#endregion
//#region src/utils/createVirtualRef.ts
const createVirtualRef = (editor, at, { fallbackRect } = {}) => ({ current: { getBoundingClientRect: () => {
	const rect = getBoundingClientRect(editor, at) || fallbackRect;
	if (!rect) throw new Error("Could not get the bounding client rect of the location. Please provide a fallbackRect.");
	return rect;
} } });

//#endregion
//#region src/utils/getDOMSelectionBoundingClientRect.ts
/** Get bounding client rect of the editor selection */
const getDOMSelectionBoundingClientRect = () => {
	const domSelection = window.getSelection();
	if (!domSelection || domSelection.rangeCount < 1) return getDefaultBoundingClientRect();
	return domSelection.getRangeAt(0).getBoundingClientRect();
};

//#endregion
//#region src/utils/getRangeBoundingClientRect.ts
/** Get bounding client rect by slate range */
const getRangeBoundingClientRect = (editor, at) => {
	if (!at) return getDefaultBoundingClientRect();
	const domRange = editor.api.toDOMRange(at);
	if (!domRange) return getDefaultBoundingClientRect();
	return domRange.getBoundingClientRect();
};

//#endregion
//#region src/utils/getSelectionBoundingClientRect.ts
/** Get bounding client rect of the editor selection */
const getSelectionBoundingClientRect = (editor) => {
	if (editor.api.isExpanded()) return getRangeBoundingClientRect(editor, editor.selection);
	return getDefaultBoundingClientRect();
};

//#endregion
export { FloatingArrow, FloatingDelayGroup, FloatingFocusManager, FloatingNode, FloatingOverlay, FloatingPortal, FloatingTree, arrow, autoPlacement, autoUpdate, computePosition, createVirtualElement, createVirtualRef, detectOverflow, flip, getBoundingClientRect, getDOMSelectionBoundingClientRect, getDefaultBoundingClientRect, getOverflowAncestors, getRangeBoundingClientRect, getSelectionBoundingClientRect, hide, inline, limitShift, makeClientRect, mergeClientRects, offset, safePolygon, shift, size, useClick, useDelayGroup, useDelayGroupContext, useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useFloatingPortalNode, useFloatingToolbar, useFloatingToolbarState, useFloatingTree, useFocus, useHover, useId, useInteractions, useListNavigation, useRole, useTypeahead, useVirtualFloating };